import { useState, useRef, useEffect } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Send, Bot, User, Sparkles, BookOpen, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import MathContent from "../components/MathContent";

const AITutor = () => {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hi! I'm Archie, your AI tutor! 🤖 I'm here to help you ace your tests. You can ask me about SAT, ACT, ASVAB, STAAR, or US Citizenship topics. What would you like to learn about today?"
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await api.chatWithTutor(userMessage, selectedCategory === "all" ? null : selectedCategory);
      setMessages(prev => [...prev, { role: "assistant", content: response.response }]);
    } catch (error) {
      toast.error("Failed to get response from tutor");
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Sorry, I'm having trouble connecting right now. Please try again in a moment!" 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const quickPrompts = [
    "Explain the Pythagorean theorem",
    "Help me with SAT reading tips",
    "What are the 3 branches of government?",
    "ASVAB math strategies"
  ];

  const categories = [
    { value: "all", label: "All Topics" },
    { value: "sat", label: "SAT" },
    { value: "act", label: "ACT" },
    { value: "asvab", label: "ASVAB" },
    { value: "staar", label: "STAAR" },
    { value: "citizenship", label: "US Citizenship" }
  ];

  return (
    <div className="h-[calc(100vh-140px)] lg:h-[calc(100vh-100px)] flex flex-col" data-testid="ai-tutor-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-indigo-500 rounded-2xl flex items-center justify-center shadow-[0_3px_0_0_#4f46e5]">
            <Bot className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-xl text-slate-800">Archie AI Tutor</h1>
            <p className="text-sm text-slate-500">Ask me anything about your tests!</p>
          </div>
        </div>

        {/* Category Filter */}
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-40" data-testid="category-select">
            <SelectValue placeholder="All Topics" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((cat) => (
              <SelectItem key={cat.value} value={cat.value}>
                {cat.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 bg-white rounded-3xl border-2 border-slate-100 shadow-[0_6px_0_0_#f1f5f9] overflow-hidden flex flex-col">
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex gap-3 ${message.role === "user" ? "flex-row-reverse" : ""} animate-slide-up`}
            >
              {/* Avatar */}
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                message.role === "assistant" 
                  ? "bg-indigo-100" 
                  : "bg-lime-100"
              }`}>
                {message.role === "assistant" ? (
                  <Bot className="w-5 h-5 text-indigo-600" />
                ) : (
                  <User className="w-5 h-5 text-lime-600" />
                )}
              </div>

              {/* Message Bubble */}
              <div className={`max-w-[80%] p-4 rounded-2xl ${
                message.role === "assistant"
                  ? "bg-slate-50 text-slate-700"
                  : "bg-lime-500 text-white"
              }`}>
                <div className="whitespace-pre-wrap"><MathContent text={message.content} /></div>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex gap-3 animate-slide-up">
              <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                <Bot className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="bg-slate-50 p-4 rounded-2xl">
                <div className="flex items-center gap-2 text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Archie is thinking...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Quick Prompts */}
        {messages.length === 1 && (
          <div className="px-4 pb-4">
            <p className="text-sm text-slate-400 mb-2 font-medium">Try asking:</p>
            <div className="flex flex-wrap gap-2">
              {quickPrompts.map((prompt, index) => (
                <button
                  key={index}
                  onClick={() => setInput(prompt)}
                  className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-full text-sm text-slate-600 font-medium transition-colors"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="p-4 border-t-2 border-slate-100">
          <div className="flex gap-3">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask Archie anything..."
              data-testid="tutor-input"
              className="flex-1 input-pop"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || loading}
              data-testid="send-btn"
              className="w-12 h-12 bg-lime-500 rounded-xl flex items-center justify-center text-white shadow-[0_3px_0_0_#65a30d] hover:shadow-[0_2px_0_0_#65a30d] hover:translate-y-[1px] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Tips */}
      <div className="mt-4 bg-indigo-50 rounded-2xl p-4 border-2 border-indigo-100">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-indigo-800 text-sm">Pro Tip</p>
            <p className="text-indigo-600 text-sm">Select a specific test category for more focused answers!</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AITutor;
